import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import { Upload, CheckCircle, FileText, Shield, Users, Target, X, AlertCircle, Send, Search, UserCheck, Handshake } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
const MAX_FILE_SIZE = 48 * 1024 * 1024;
export function SpecialistsPage() {
  const [dragActive, setDragActive] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedRank, setSelectedRank] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const {
    t
  } = useLanguage();
  // Data from translations
  const benefits = [{
    icon: FileText,
    ...t('specialists.benefits.items.profile')
  }, {
    icon: Shield,
    ...t('specialists.benefits.items.docs')
  }, {
    icon: Users,
    ...t('specialists.benefits.items.matching')
  }, {
    icon: Target,
    ...t('specialists.benefits.items.access')
  }];
  const steps = [{
    number: '01',
    icon: Send,
    ...t('specialists.steps.items')[0]
  }, {
    number: '02',
    icon: Search,
    ...t('specialists.steps.items')[1]
  }, {
    number: '03',
    icon: UserCheck,
    ...t('specialists.steps.items')[2]
  }, {
    number: '04',
    icon: Handshake,
    ...t('specialists.steps.items')[3]
  }];
  const requirements = t('specialists.requirements.items');
  const faqs = t('specialists.faq.items');
  const rankOptions = [{
    category: t('specialists.form.ranks.deck'),
    options: [t('specialists.form.ranks.options.master'), t('specialists.form.ranks.options.chiefMate'), t('specialists.form.ranks.options.secondMate'), t('specialists.form.ranks.options.thirdMate'), t('specialists.form.ranks.options.bosun'), t('specialists.form.ranks.options.ab'), t('specialists.form.ranks.options.os')]
  }, {
    category: t('specialists.form.ranks.engine'),
    options: [t('specialists.form.ranks.options.chiefEng'), t('specialists.form.ranks.options.secondEng'), t('specialists.form.ranks.options.thirdEng'), t('specialists.form.ranks.options.fourthEng'), t('specialists.form.ranks.options.eto'), t('specialists.form.ranks.options.asstEto'), t('specialists.form.ranks.options.motorman'), t('specialists.form.ranks.options.wiper')]
  }, {
    category: t('specialists.form.ranks.other'),
    options: [t('specialists.form.ranks.options.dpo'), t('specialists.form.ranks.options.sdpo'), t('specialists.form.ranks.options.craneOp'), t('specialists.form.ranks.options.rigger'), t('specialists.form.ranks.options.electrician'), t('specialists.form.ranks.options.mechanic'), t('specialists.form.ranks.options.other')]
  }];
  const vesselTypes = [{
    value: 'tanker',
    label: t('specialists.form.vessels.tanker')
  }, {
    value: 'bulk',
    label: t('specialists.form.vessels.bulk')
  }, {
    value: 'container',
    label: t('specialists.form.vessels.container')
  }, {
    value: 'offshore',
    label: t('specialists.form.vessels.offshore')
  }, {
    value: 'cruise',
    label: t('specialists.form.vessels.cruise')
  }, {
    value: 'other',
    label: t('specialists.form.vessels.other')
  }];
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const validateFile = (file: File): boolean => {
    setFileError('');
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds 48 MB limit');
      return false;
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Invalid file type. Please upload PDF, DOC, DOCX, or image files (PNG, JPG, GIF, WebP)');
      return false;
    }
    return true;
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setUploadedFile(file);
      }
    }
  };
  const removeFile = () => {
    setUploadedFile(null);
    setFileError('');
  };
  const handleRankChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedRank(value);
    setShowOtherInput(value === t('specialists.form.ranks.options.other'));
  };
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  return <div className="min-h-screen bg-white" data-id="element-509">
      <SEOHead page="specialists" data-id="element-510" />
      <Header data-id="element-511" />

      {/* Hero Section */}
      <section className="w-full px-6 md:px-12 py-8" data-id="element-512">
        <div className="max-w-7xl mx-auto" data-id="element-513">
          <div className="hidden md:block relative w-full rounded-2xl overflow-hidden" data-id="element-514">
            <img src="https://cdn.magicpatterns.com/uploads/o1c9rQhNQZG9dHqBoXXqmj/sunrise-busy-industrial-port-with-ships.jpg" alt="Industrial port at sunrise" className="w-full h-[600px] lg:h-[700px] object-cover object-center" data-id="element-515" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-516" />
            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10" data-id="element-517">
              <div className="max-w-xl" data-id="element-518">
                <h1 className="text-2xl lg:text-4xl font-bold text-white mb-3 leading-tight drop-shadow-lg" data-id="element-519">
                  {t('specialists.hero.title')}
                </h1>
                <p className="text-white/95 text-sm lg:text-base leading-relaxed mb-4 drop-shadow-md" data-id="element-520">
                  {t('specialists.hero.desc')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3" data-id="element-521">
                  <a href="#cv-form" className="px-6 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity shadow-lg inline-flex items-center justify-center gap-2" data-id="element-522">
                    <Upload className="w-4 h-4" data-id="element-523" />
                    {t('specialists.hero.submitCV')}
                  </a>
                  <a href="#opportunities" className="px-6 py-3 bg-white/95 text-[var(--color-dark)] text-sm font-medium rounded-md hover:bg-white transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-524">
                    {t('specialists.hero.viewOpportunities')}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="md:hidden space-y-6" data-id="element-525">
            <h1 className="text-3xl font-bold text-[var(--color-dark)] leading-tight text-center" data-id="element-526">
              {t('specialists.hero.title')}
            </h1>
            <div className="relative w-full rounded-2xl overflow-hidden" data-id="element-527">
              <img src="https://cdn.magicpatterns.com/uploads/o1c9rQhNQZG9dHqBoXXqmj/sunrise-busy-industrial-port-with-ships.jpg" alt="Industrial port" className="w-full h-[300px] object-cover" style={{
              objectPosition: '75% 50%'
            }} data-id="element-528" />
            </div>
            <p className="text-[var(--color-dark)]/80 text-sm leading-relaxed text-center" data-id="element-529">
              {t('specialists.hero.desc')}
            </p>
            <div className="flex flex-col gap-3" data-id="element-530">
              <a href="#cv-form" className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2" data-id="element-531">
                <Upload className="w-4 h-4" data-id="element-532" />
                {t('specialists.hero.submitCV')}
              </a>
              <a href="#opportunities" className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity inline-flex items-center justify-center" data-id="element-533">
                {t('specialists.hero.viewOpportunities')}
              </a>
            </div>
          </div>

          <div className="text-center mt-6 space-y-2" data-id="element-534">
            <p className="text-[var(--color-text-light)] text-sm" data-id="element-535">
              {t('specialists.hero.tagline1')}
            </p>
            <p className="text-[var(--color-text-light)] text-sm" data-id="element-536">
              {t('specialists.hero.tagline2')}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-537">
        <div className="max-w-7xl mx-auto" data-id="element-538">
          <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-8" data-id="element-539">
            <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-540">
              {t('specialists.benefits.pill')}
            </span>
          </div>

          <div className="flex flex-col lg:flex-row gap-12" data-id="element-541">
            <div className="lg:w-2/5" data-id="element-542">
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-6 leading-tight whitespace-pre-line" data-id="element-543">
                {t('specialists.benefits.title')}
              </h2>
              <p className="text-[var(--color-text-light)] text-sm leading-relaxed mb-4" data-id="element-544">
                {t('specialists.benefits.desc1')}
              </p>
              <p className="text-[var(--color-text-light)] text-sm leading-relaxed" data-id="element-545">
                {t('specialists.benefits.desc2')}
              </p>
            </div>

            <div className="lg:w-3/5" data-id="element-546">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-547">
                {benefits.map((benefit, index) => <div key={index} className="p-6 bg-white border border-gray-100 rounded-xl" data-id="element-548">
                    <benefit.icon className="w-6 h-6 text-[var(--color-tan)] mb-4" strokeWidth={1.5} data-id="element-549" />
                    <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-550">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-[var(--color-text-light)]" data-id="element-551">
                      {benefit.desc}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Zigzag with Cards */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-552">
        <div className="max-w-5xl mx-auto" data-id="element-553">
          <div className="text-center mb-10" data-id="element-554">
            <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-5 bg-white" data-id="element-555">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-556">
                {t('specialists.steps.pill')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-3" data-id="element-557">
              {t('specialists.steps.title')}
            </h2>
            <p className="text-base text-gray-600 max-w-xl mx-auto" data-id="element-558">
              {t('specialists.steps.desc')}
            </p>
          </div>

          {/* Desktop: Zigzag Cards with Connecting Lines */}
          <div className="hidden md:block relative" data-id="element-559">
            {/* Main vertical center line */}
            <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-gray-200" data-id="element-560" />

            <div className="space-y-5 relative" data-id="element-561">
              {steps.map((step, index) => {
              const isRight = index % 2 === 0;
              const StepIcon = step.icon;
              return <div key={index} className="relative" data-id="element-562">
                    {/* Horizontal branch line */}
                    <div className={`absolute top-1/2 -translate-y-1/2 h-px bg-gray-200 ${isRight ? 'left-1/2 w-[8%]' : 'right-1/2 w-[8%]'}`} data-id="element-563" />

                    {/* Center dot */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[var(--color-tan)] z-10" data-id="element-564" />

                    {/* Card */}
                    <div className={`flex ${isRight ? 'justify-end' : 'justify-start'}`} data-id="element-565">
                      <div className="w-[42%]" data-id="element-566">
                        <div className="bg-white border border-gray-200 rounded-xl p-5 relative" data-id="element-567">
                          <span className="absolute top-3 right-4 text-xs font-medium text-gray-400" data-id="element-568">
                            {step.number}
                          </span>
                          <div className="flex items-center gap-4 pr-10" data-id="element-569">
                            <div className="w-10 h-10 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-570">
                              <StepIcon className="w-5 h-5 text-[var(--color-tan)]" data-id="element-571" />
                            </div>
                            <div className="flex-1" data-id="element-572">
                              <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-573">
                                {step.title}
                              </h3>
                              <p className="text-sm text-gray-500 leading-relaxed" data-id="element-574">
                                {step.desc}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Mobile: Vertical cards with connecting line */}
          <div className="md:hidden relative" data-id="element-575">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" data-id="element-576" />

            <div className="space-y-4" data-id="element-577">
              {steps.map((step, index) => {
              const StepIcon = step.icon;
              return <div key={index} className="relative flex gap-4" data-id="element-578">
                    <div className="w-8 flex-shrink-0 flex justify-center relative z-10" data-id="element-579">
                      <div className="w-2 h-2 rounded-full bg-[var(--color-tan)] mt-6" data-id="element-580" />
                    </div>
                    <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 relative" data-id="element-581">
                      <span className="absolute top-3 right-3 text-xs font-medium text-gray-400" data-id="element-582">
                        {step.number}
                      </span>
                      <div className="flex items-center gap-3 pr-8" data-id="element-583">
                        <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-584">
                          <StepIcon className="w-4 h-4 text-[var(--color-tan)]" data-id="element-585" />
                        </div>
                        <div className="flex-1" data-id="element-586">
                          <h3 className="text-sm font-semibold text-[var(--color-text-dark)] mb-0.5" data-id="element-587">
                            {step.title}
                          </h3>
                          <p className="text-xs text-gray-500" data-id="element-588">{step.desc}</p>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-589">
        <div className="max-w-4xl mx-auto text-center" data-id="element-590">
          <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6" data-id="element-591">
            <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-592">
              {t('specialists.faq.pill')}
            </span>
          </div>
          <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-12" data-id="element-593">
            {t('specialists.faq.title')}
          </h2>

          <div className="bg-white rounded-2xl p-6 md:p-8 border-2 border-gray-200 text-left" data-id="element-594">
            <div className="space-y-0" data-id="element-595">
              {faqs.map((faq: any, index: number) => <div key={index} className="border-b border-gray-100 last:border-0" data-id="element-596">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full py-5 flex items-start justify-between text-left gap-4" aria-expanded={openFaq === index} data-id="element-597">
                    <span className="text-base font-medium text-[var(--color-text-dark)]" data-id="element-598">
                      {faq.q}
                    </span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === index ? 'bg-[var(--color-tan)]' : 'bg-gray-100'}`} data-id="element-599">
                      <svg className={`w-4 h-4 transition-colors ${openFaq === index ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-600">
                        {openFaq === index ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-601" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" data-id="element-602" />}
                      </svg>
                    </div>
                  </button>
                  {/* Content stays in DOM for SEO - hidden via CSS */}
                  <div className={`overflow-hidden transition-all duration-200 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-603">
                    <p className="pb-5 text-sm text-[var(--color-text-light)]" data-id="element-604">
                      {faq.a}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-605">
        <div className="max-w-4xl mx-auto text-center" data-id="element-606">
          <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white" data-id="element-607">
            <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-608">
              {t('specialists.requirements.pill')}
            </span>
          </div>
          <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-609">
            {t('specialists.requirements.title')}
          </h2>
          <p className="text-lg text-[var(--color-text-light)] mb-8" data-id="element-610">
            {t('specialists.requirements.desc')}
          </p>

          <div className="bg-white border-2 border-gray-200 rounded-xl p-8 text-left" data-id="element-611">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-612">
              {requirements.map((req: string, index: number) => <div key={index} className="flex items-start gap-3" data-id="element-613">
                  <CheckCircle className="w-5 h-5 text-[var(--color-tan)] flex-shrink-0 mt-0.5" data-id="element-614" />
                  <span className="text-sm text-[var(--color-text-dark)]" data-id="element-615">
                    {req}
                  </span>
                </div>)}
            </div>
            <p className="text-xs text-[var(--color-text-light)] italic border-t border-gray-200 pt-4" data-id="element-616">
              {t('specialists.requirements.note')}
            </p>
          </div>
        </div>
      </section>

      {/* CV Form */}
      <section id="cv-form" className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-617">
        <div className="max-w-4xl mx-auto text-center" data-id="element-618">
          <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6" data-id="element-619">
            <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-620">
              {t('specialists.form.pill')}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-621">
            {t('specialists.form.title')}
          </h2>
          <p className="text-lg text-[var(--color-text-light)] mb-2" data-id="element-622">
            {t('specialists.form.desc1')}
          </p>
          <p className="text-base text-[var(--color-text-light)] font-medium mb-8" data-id="element-623">
            {t('specialists.form.desc2')}
          </p>

          <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8 text-left" data-id="element-624">
            <form className="space-y-5" data-id="element-625">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-626">
                <div data-id="element-627">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-628">
                    {t('specialists.form.labels.firstName')}{' '}
                    <span className="text-red-500" data-id="element-629">*</span>
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-630" />
                </div>
                <div data-id="element-631">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-632">
                    {t('specialists.form.labels.lastName')}{' '}
                    <span className="text-red-500" data-id="element-633">*</span>
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-634" />
                </div>
              </div>

              <div data-id="element-635">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-636">
                  {t('specialists.form.labels.rank')}{' '}
                  <span className="text-red-500" data-id="element-637">*</span>
                </label>
                <select value={selectedRank} onChange={handleRankChange} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center'
              }} data-id="element-638">
                  <option value="" data-id="element-639">
                    {t('specialists.form.labels.selectRank')}
                  </option>
                  {rankOptions.map(group => <optgroup key={group.category} label={group.category} data-id="element-640">
                      {group.options.map(option => <option key={option} value={option} data-id="element-641">
                          {option}
                        </option>)}
                    </optgroup>)}
                </select>
              </div>

              {showOtherInput && <div data-id="element-642">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-643">
                    {t('specialists.form.labels.specify')}{' '}
                    <span className="text-red-500" data-id="element-644">*</span>
                  </label>
                  <input type="text" placeholder={t('specialists.form.labels.enterPosition')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-645" />
                </div>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-646">
                <div data-id="element-647">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-648">
                    {t('specialists.form.labels.email')}{' '}
                    <span className="text-red-500" data-id="element-649">*</span>
                  </label>
                  <input type="email" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-650" />
                </div>
                <div data-id="element-651">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-652">
                    {t('specialists.form.labels.phone')}{' '}
                    <span className="text-red-500" data-id="element-653">*</span>
                  </label>
                  <input type="tel" placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-654" />
                  <p className="text-xs text-[var(--color-text-light)] mt-1" data-id="element-655">
                    {t('specialists.form.labels.phoneHint')}
                  </p>
                </div>
              </div>

              <div data-id="element-656">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-657">
                  {t('specialists.form.labels.cv')}{' '}
                  <span className="text-red-500" data-id="element-658">*</span>
                </label>

                {!uploadedFile ? <div data-id="element-659">
                    <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${dragActive ? 'border-[var(--color-tan)] bg-[var(--color-tan)]/5' : 'border-gray-300 hover:border-gray-400'}`} onClick={() => document.getElementById('cv-upload')?.click()} data-id="element-660">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-661" />
                      <p className="text-sm text-[var(--color-text-dark)] mb-1" data-id="element-662">
                        {t('specialists.form.labels.dragDrop')}
                      </p>
                      <p className="text-xs text-[var(--color-text-light)]" data-id="element-663">
                        {t('specialists.form.labels.fileTypes')}
                      </p>
                      <input id="cv-upload" type="file" accept=".pdf,.doc,.docx,image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleFileChange} className="hidden" data-id="element-664" />
                    </div>
                    {fileError && <div className="mt-2 flex items-start gap-2 text-red-500 text-sm" data-id="element-665">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-666" />
                        <span data-id="element-667">{fileError}</span>
                      </div>}
                  </div> : <div className="border-2 border-[var(--color-tan)] bg-[var(--color-tan)]/5 rounded-lg p-4 flex items-center justify-between" data-id="element-668">
                    <div className="flex items-center gap-3" data-id="element-669">
                      <FileText className="w-8 h-8 text-[var(--color-tan)]" data-id="element-670" />
                      <div data-id="element-671">
                        <p className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-672">
                          {uploadedFile.name}
                        </p>
                        <p className="text-xs text-[var(--color-text-light)]" data-id="element-673">
                          {formatFileSize(uploadedFile.size)}
                        </p>
                      </div>
                    </div>
                    <button type="button" onClick={removeFile} className="p-2 hover:bg-red-50 rounded-full transition-colors" data-id="element-674">
                      <X className="w-5 h-5 text-red-500" data-id="element-675" />
                    </button>
                  </div>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-676">
                <div data-id="element-677">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-678">
                    {t('specialists.form.labels.vesselType')}{' '}
                    <span className="text-red-500" data-id="element-679">*</span>
                  </label>
                  <select className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center'
                }} data-id="element-680">
                    <option value="" data-id="element-681">
                      {t('specialists.form.labels.selectVessel')}
                    </option>
                    {vesselTypes.map(type => <option key={type.value} value={type.value} data-id="element-682">
                        {type.label}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-683">
                  <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-684">
                    {t('specialists.form.labels.yearsExp')}{' '}
                    <span className="text-red-500" data-id="element-685">*</span>
                  </label>
                  <input type="number" placeholder="e.g., 5" min="0" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-686" />
                </div>
              </div>

              <div data-id="element-687">
                <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-688">
                  {t('specialists.form.labels.availability')}{' '}
                  <span className="text-red-500" data-id="element-689">*</span>
                </label>
                <input type="date" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-690" />
                <p className="text-xs text-[var(--color-text-light)] mt-1" data-id="element-691">
                  {t('specialists.form.labels.availabilityHint')}
                </p>
              </div>

              <div className="pt-4" data-id="element-692">
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-693">
                  <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 text-[var(--color-tan)] border-gray-300 rounded focus:ring-[var(--color-tan)]" data-id="element-694" />
                  <span className="text-sm text-[var(--color-text-dark)]" data-id="element-695">
                    {t('specialists.form.labels.consent')}{' '}
                    <span className="text-red-500" data-id="element-696">*</span>
                  </span>
                </label>
              </div>

              <div className="flex justify-center pt-2" data-id="element-697">
                <button type="submit" disabled={!agreedToTerms} className={`px-8 py-4 text-white text-base font-medium rounded-lg transition-opacity flex items-center gap-2 ${agreedToTerms ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-698">
                  <CheckCircle className="w-5 h-5" data-id="element-699" />
                  {t('specialists.form.labels.submit')}
                </button>
              </div>

              <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-700">
                {t('specialists.form.labels.disclaimer')}
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-dark)]" data-id="element-701">
        <div className="max-w-4xl mx-auto text-center" data-id="element-702">
          <div className="inline-block px-4 py-1.5 border border-gray-600 rounded-full mb-6" data-id="element-703">
            <span className="text-sm font-medium text-white" data-id="element-704">
              {t('specialists.contact.pill')}
            </span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-705">
            {t('specialists.contact.title')}
          </h2>
          <p className="text-lg text-gray-400 mb-8" data-id="element-706">
            {t('specialists.contact.desc')}
          </p>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-707">
            <a href="https://t.me/elvora" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-708">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-709">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-710" />
              </svg>
              <span className="text-sm font-medium" data-id="element-711">Telegram</span>
            </a>
            <a href="https://wa.me/380" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-712">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-713">
                <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2z" data-id="element-714" />
              </svg>
              <span className="text-sm font-medium" data-id="element-715">WhatsApp</span>
            </a>
            <a href="mailto:info@elvora.com" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-716">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-717">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-718" />
              </svg>
              <span className="text-sm font-medium" data-id="element-719">Email</span>
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-720" />
    </div>;
}